/**
 * The assistant (0.9.5): answers questions about this app only, from the staff and admin guides and a
 * read-only slice of the schedule. It explains and points to a screen; it never acts.
 */
export type AssistantLang = 'en' | 'es';

/** on = the Ask button shows; off = the admin switched it off; not-configured = the AOAI_* settings are missing. */
export type AssistantState = 'on' | 'off' | 'not-configured';

export const ASSISTANT_SETTING_KEY = 'ASSISTANT_ENABLED';
/** The cost guard: questions per person per day, and for everyone together. */
export const ASSISTANT_USER_DAILY = 60;
export const ASSISTANT_GLOBAL_DAILY = 1000;
export const ASSISTANT_MAX_QUESTION = 600;

export interface AssistantAskRequest {
  question: string;
  lang: AssistantLang;
  /** 1.7.9: the page the question was asked from. */
  path?: string;
  /** 1.8.0: "What can I do here?" (here) and "Walk me through…" (walk) are presses, not typed questions. */
  mode?: AssistantMode;
  /** 1.8.0: the conversation so far, exactly as the last answer gave it back. */
  thread?: AssistantThread;
  /** 1.8.0: the chip the person tapped under the last question. */
  pick?: string;
}

/* ---------------- 1.8.0: ask before you answer, then numbered steps ---------------- */

export type AssistantMode = 'ask' | 'here' | 'walk';

/**
 * What an answer is: `answer` (words from the guides, as before), `clarify` (a short question with chips — nothing
 * was sent to the model), `steps` (numbered steps in the app's own words), `here` (what can be pressed on this
 * screen), `none` (the app does not do that today — with the nearest thing it does).
 * 1.8.4 (D-1.8.4-5): `program` (a program's agreement and rules, from its own document, in plain words) and `today`
 * (what is happening, read from what Today shows — never acted on). Neither calls the model.
 */
export type AssistantKind = 'answer' | 'clarify' | 'steps' | 'here' | 'none' | 'program' | 'today' | 'data';
export const ASSISTANT_KINDS = ['answer', 'clarify', 'steps', 'here', 'none', 'program', 'today', 'data'] as const satisfies readonly AssistantKind[];

/** The conversation the server needs to carry on: it is sent back with the next press. Never a person's data. */
export interface AssistantThread {
  /** The screen the conversation is about (shared/app-screens.ts key). */
  screen?: string;
  /** What the person wants to do, once known (the question map's intent id). */
  intent?: string;
  /** The answers so far: clarifier id → answer id. */
  picks: Record<string, string>;
  /** How many questions were asked back (never more than ASSISTANT_MAX_CLARIFIERS). */
  asked: number;
  /** The question that is waiting for an answer (a clarifier id, or ASSISTANT_INTENT_QUESTION). */
  pending?: string;
  /** What the person typed, joined: a later question is skipped when these words already answer it. */
  said: string;
  /** A path to walk at once: the nearest thing under "the app doesn't do that today", tapped. */
  path?: string;
}

/** 1.8.4: the id of the question "Which program?" (its chips are Housekeeping · Companion & Wellness · Reading & Services · Tech Support). */
export const ASSISTANT_PROGRAM_QUESTION = '__program';

/** 1.8.4: which program an agreement answer is about, and which part of it ('summary': all of it; 'each': one line a program). */
export interface AssistantProgramRef {
  key: string;
  section: 'allowed' | 'notAllowed' | 'agrees' | 'signed' | 'summary' | 'each';
}

/** 1.8.4: what a what's-happening answer was read from. */
export type AssistantTodayTopic = 'unconfirmed' | 'emails' | 'waiting' | 'day' | 'private';

/** At most this many questions before an answer (D-1.8.0-2). */
export const ASSISTANT_MAX_CLARIFIERS = 2;
/** The id of the question "what do you want to do on this screen?" (its chips are the screen's intents). */
export const ASSISTANT_INTENT_QUESTION = '__intent';
/** At most this many lines under "What can I do here?" (D-1.8.0-3). */
export const ASSISTANT_HERE_LINES = 5;
/** The knowledge sent with a question: the best chunks for the screen and the intent, never the whole of it. */
export const ASSISTANT_TOP_CHUNKS = 12;

export interface AssistantChip {
  id: string;
  label: string;
}

export interface AssistantClarify {
  /** Which question this is (a clarifier id, or ASSISTANT_INTENT_QUESTION). */
  id: string;
  question: string;
  chips: AssistantChip[];
  thread: AssistantThread;
}

export interface AssistantStep {
  n: number;
  /** The step, buttons named in quotes exactly as the app shows them. */
  text: string;
  /** The screen this step is on, in the asker's language ("Events"). */
  screen: string;
  /** What the person will see after it, when the map says. */
  see?: string;
  /** Present when the step has a control to point at and the asker's role can open its screen. */
  showMe: AssistantShowMe | null;
}

/** One line of "What can I do here?": a thing the person can press. */
export interface AssistantHereLine {
  /** The control's name as the screen shows it. */
  control: string;
  /** What it does, one short sentence. */
  does: string;
  showMe: AssistantShowMe | null;
}

/** The nearest thing the app does, offered under "the app doesn't do that today". */
export interface AssistantNearest {
  label: string;
  /** Tapping it walks that path: the thread to send back with `mode: 'ask'`. */
  thread: AssistantThread;
}

export interface AssistantLink {
  /** Words for the link, in the asker's language ("Today → Actions on a visit"). */
  label: string;
  route: string;
}

/**
 * "Show me" (1.4.2): the screen a how-do-I answer ends on and the control to point at — CSS selectors, the
 * first one present on the page wins. It only navigates; it never presses anything.
 */
export interface AssistantShowMe {
  route: string;
  targets: string[];
  /** The screen's name in the asker's language ("Look up"). */
  label: string;
  /**
   * 1.7.9: the control lives on the page the asker may already have open (an event's own page): when the address
   * matches one of these patterns Show me stays where it is and points there; otherwise it goes to `route`.
   */
  stayOn?: string[];
}

export interface AssistantAnswer {
  answer: string;
  links: AssistantLink[];
  /** A how-do-I answer that ends on a screen; null otherwise. */
  showMe: AssistantShowMe | null;
  /** The guide sections the answer was grounded on ("Staff guide §4 Cancel or reschedule"). */
  sources: string[];
  /** Tokens the call used (null for the offline sample answerer). */
  tokens: number | null;
  /** True when the answer came from the offline sample answerer (mock driver, no Azure OpenAI). */
  offline: boolean;
  /** Questions this person has left today. */
  remainingToday: number;
  /** 1.8.0 — what this answer is (absent on an answer from before 1.8.0: read it as 'answer'). */
  kind?: AssistantKind;
  /** 1.8.0 — the app version whose knowledge the answer stands on ("Based on version 1.8.0's guide"). */
  version?: string;
  /** 1.8.0 — the role check, said first ("You're signed in as administrator, so you can do this."). */
  roleLine?: string;
  /** 1.8.0 — false when the asker's role cannot do what the steps describe. */
  allowed?: boolean;
  clarify?: AssistantClarify;
  steps?: AssistantStep[];
  here?: AssistantHereLine[];
  nearest?: AssistantNearest | null;
  /** 1.8.4 — the Show me button's own words when they are not "Show me" ("Open the full agreement"). */
  showMeText?: string;
  /** 1.8.4 — an agreement answer: the program and the part. */
  program?: AssistantProgramRef;
  /** 1.8.4 — a what's-happening answer: what it read, and the day (yyyyMMdd) when it read one. */
  today?: { topic: AssistantTodayTopic; day: string | null };
  /** 2 Nov 2026 (D-1103-ASK) — a question about our own data: one sentence and a short list, each line with one button that opens the screen. */
  data?: AssistantDataAnswer;
}

/** 2 Nov 2026 (D-1103-ASK-1): the menu of questions Ask can answer from our own data (shared/assistant-data.ts). */
export type AssistantDataIntent = 'unconfirmed' | 'dayVisits' | 'needsNewDay' | 'companionOpen' | 'eventWho' | 'agreementOpened' | 'closeoutLeft' | 'signedUp';

export interface AssistantDataItem {
  /** The line, as the screen would say it ("SECURITY-TEST Ana Rivera · 2nd visit · 9:50 AM"). */
  label: string;
  /** The button's words ("Open the visit"), and the address it opens. */
  button: string;
  route: string;
}

export interface AssistantDataAnswer {
  intent: AssistantDataIntent;
  /** The one sentence that is announced. */
  sentence: string;
  items: AssistantDataItem[];
  /** Lines left out of the list (the list holds at most ASSISTANT_DATA_LIST_MAX). */
  more: number;
}

/** The Admin → Settings switch "Ask can answer questions about our data" (default Yes). */
export const ASSISTANT_DATA_SETTING_KEY = 'ASSISTANT_DATA_ENABLED';
/** A short list: this many lines, then "and n more" with a button to the screen. */
export const ASSISTANT_DATA_LIST_MAX = 12;

export interface AssistantStatus {
  state: AssistantState;
  /** The Admin → Settings switch: null when the site has no ASSISTANT_ENABLED row yet (the default applies). */
  setting: boolean | null;
  configured: boolean;
  usedToday: number;
  userLimit: number;
  totalToday: number;
  dailyLimit: number;
  tokensToday: number;
  tokensYesterday: number;
}

export type AssistantExplainRequest =
  | { kind: 'health'; key: string; lang: AssistantLang }
  | { kind: 'review'; queue: 'q1' | 'q2' | 'q3'; id: number; lang: AssistantLang };

export interface AssistantFeedbackRequest {
  helpful: boolean;
  /** 1.8.0 — the question that was rated (kept in the audit with names, emails, phones and apartments taken out). */
  question?: string;
  kind?: AssistantKind;
  /** The screen it was asked from (an address without its ids is kept: "/events/…"). */
  path?: string;
}

/** 1.8.0 — the audit event a thumbs up or down writes. */
export const ASSISTANT_RATED_EVENT = 'ASSISTANT_RATED';

/* ---------------- 1.4.0 Careful translation (a second opinion on the crew's words) ---------------- */

/** Where the crew's words sit: the close-out (SM_ServiceCompletions) or its unit condition report. */
export type CarefulScope = 'completion' | 'assessment';
export const CAREFUL_FIELDS = {
  completion: ['notes', 'incident', 'condition', 'followUpSummary'],
  assessment: ['observationSummary', 'areasAffected', 'escalationNotes', 'recommendedFollowUp', 'otherDescription'],
} as const satisfies Record<CarefulScope, readonly string[]>;
export type CarefulField = (typeof CAREFUL_FIELDS)[CarefulScope][number];

export interface CarefulTranslationRequest {
  scope: CarefulScope;
  field: CarefulField;
  lang: AssistantLang;
}

export interface CarefulTranslation {
  /** The crew's words exactly as stored (always shown first; never replaced). */
  original: string;
  /** The assistant's English, `[?]` after any word it was unsure of. */
  text: string;
  /** True for the offline stand-in (mock driver + ASSISTANT_OFFLINE=1) — never a real translation. */
  offline: boolean;
  /** True when the answer came from the app's cache (no new call, nothing counted). */
  cached: boolean;
  at: string;
  /** Questions this person has left today (null when nothing was asked: a cached answer). */
  remainingToday: number | null;
}

/** 1.8.0 (D-1.8.0-4) — Health: the assistant's calls and cost, a row a day (newest first). */
export interface AssistantUsageDay {
  /** yyyy-MM-dd, New York. */
  day: string;
  /** Calls to Azure OpenAI (a question back, "What can I do here?" and "not today" make none). */
  calls: number;
  tokens: number;
  /** In dollars, when AOAI_COST_PER_1K_TOKENS is set; null otherwise. */
  cost: number | null;
}

export interface AssistantUsageReport {
  days: AssistantUsageDay[];
  /** The price used for a thousand tokens (AOAI_COST_PER_1K_TOKENS), null when it is not set. */
  costPer1k: number | null;
}
